// src/components/NewAgentChat.tsx
// Specs Q&A Agent with Proof-of-Work Citations (like Cursor)
import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Send, ArrowUp, ChevronDown, FileText, ExternalLink, Copy, Check, Square, BookOpen, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../lib/supabase';
import { LaborBreakdown } from './LaborBreakdown';
import { ShorthandBreakdown } from './ShorthandBreakdown';
import '../styles/AgentChat.css';
import '../styles/LaborBreakdown.css';

export interface Citation {
  // Source type - specs, drawings, or neca_manual
  source?: 'specs' | 'drawings' | 'neca_manual';
  
  // Multi-file support (specs)
  fileId?: string;      // Database ID of the specs file
  fileName?: string;    // Display name of the file
  
  // Drawing-specific fields
  sheetName?: string;   // Sheet name like "E-101" (drawings only)
  context?: string;     // Context explanation (drawings only)
  noteNumber?: string;  // Note number like "Note 3" (drawings only)
  
  // Original fields
  pageNumber: number;
  quote: string;
  section?: string;
  analysis?: string;
  boundingBox?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

interface ThinkingStep {
  step: string;
  status: 'starting' | 'completed' | 'error';
  pagesSearched?: number[];
  result?: string;
}

// Tool response with answer and citations
interface ToolResponse {
  answer: string;
  citations: Citation[];
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  citations?: Citation[];
  thinkingSteps?: ThinkingStep[];
  confidence?: 'high' | 'medium' | 'low';
  // Tool responses for display (what each tool found)
  drawingResponse?: ToolResponse;
  specsResponse?: ToolResponse;
  laborEstimate?: {
    project_description: string;
    steps: any[];
    total_labor_normal: number;
    total_labor_difficult: number;
    total_labor_very_difficult: number;
  };
  shorthandEstimate?: {
    materialCost: number;
    laborHours: number;
    pmLaborRate: number;
    pmHours: number;
    laborRate?: number;
    overhead?: number;
    profit?: number;
    materialTaxRate?: number;
  };
}

interface NewAgentChatProps {
  estimation: any;
  specsFiles: any[];
  drawingPdfUrl?: string;
  onNavigateToSpecsPage?: (citation: Citation) => void;
  onNavigateToDrawingPage?: (pageNumber: number) => void;
  onNavigateToNecaPage?: (pageNumber: number) => void;
  onClose?: () => void;
  onNavigateToEstimate?: () => void;
  isOpen?: boolean;
  onUnreadChange?: (hasUnread: boolean) => void;
}

const NewAgentChat = forwardRef<any, NewAgentChatProps>(({ estimation, specsFiles, drawingPdfUrl, onNavigateToSpecsPage, onNavigateToDrawingPage, onNavigateToNecaPage, onClose, onNavigateToEstimate, isOpen = true, onUnreadChange }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;

  useEffect(() => {
    if (isOpen) {
      onUnreadChangeRef.current?.(false);
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [isOpen]);

  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [liveThinkingSteps, setLiveThinkingSteps] = useState<ThinkingStep[]>([]);
  const [expandedCitations, setExpandedCitations] = useState<Set<string>>(new Set());
  const [laborRate, setLaborRate] = useState<number>(200);
  const [overhead, setOverhead] = useState<number>(5);
  const [profit, setProfit] = useState<number>(15);
  const [materialTaxRate, setMaterialTaxRate] = useState<number>(3.94);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef<boolean>(false);

  // Save message to Supabase
  const saveMessageToSupabase = useCallback(async (message: Message) => {
    if (!estimation?.id) return;

    try {
      console.log('💾 Saving specs message to Supabase:', message.role);
      
      const insertData: any = {
        estimation_id: estimation.id,
        role: message.role,
        content: message.content,
        timestamp: message.timestamp,
        agent: message.laborEstimate ? 'labor' : 'specs', // Identify agent type
        // Store citations and thinking steps as JSON
        citations: message.citations ? JSON.stringify(message.citations) : null,
        thinking_steps: message.thinkingSteps ? JSON.stringify(message.thinkingSteps) : null,
        confidence: message.confidence || null,
        labor_estimate: message.laborEstimate ? JSON.stringify(message.laborEstimate) : null
      };
      
      const { error } = await supabase
        .from('estimation_chat_messages')
        .insert(insertData);
      
      if (error) {
        console.error('❌ Error saving message:', error);
      } else {
        console.log('✅ Message saved to Supabase');
      }
    } catch (error) {
      console.error('❌ Exception saving message:', error);
    }
  }, [estimation?.id]);

  // Generate UUID v4
  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Load messages from Supabase on mount
  useEffect(() => {
    if (estimation?.id) {
      loadMessagesFromSupabase();
      loadLaborRateFromEstimate();
    }
  }, [estimation?.id]);
  
  // Load labor rate, overhead, profit from Supabase or use defaults
  const loadLaborRateFromEstimate = async () => {
    if (!estimation?.id) return;
    
    try {
      // Use defaults: $200 labor rate, 5% overhead, 15% profit
      // These will be updated by the user in SimplifiedEstimateTable
      setLaborRate(200);
      setOverhead(5);
      setProfit(15);
    } catch (error) {
      console.log('Using default values');
    }
  };

  const loadMessagesFromSupabase = async () => {
    if (!estimation?.id) return;

    try {
      const { data, error } = await supabase
        .from('estimation_chat_messages')
        .select('*')
        .eq('estimation_id', estimation.id)
        .in('agent', ['specs', 'labor']) // Load both specs and labor agent messages
        .order('timestamp', { ascending: true });

      if (error) {
        console.error('❌ Error loading messages:', error);
        return;
      }

      if (data && data.length > 0) {
        // Convert database messages to Message format
        let loadedMessages: Message[] = data.map((msg: any) => ({
          id: msg.id || generateUUID(),
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          citations: msg.citations ? JSON.parse(msg.citations) : undefined,
          thinkingSteps: msg.thinking_steps ? JSON.parse(msg.thinking_steps) : undefined,
          confidence: msg.confidence,
          laborEstimate: msg.labor_estimate ? JSON.parse(msg.labor_estimate) : undefined
        }));
        
        // Deduplicate messages - remove exact duplicates based on content, role, and similar timestamp
        const uniqueMessages: Message[] = [];
        const seenContent = new Set<string>();
        
        for (const msg of loadedMessages) {
          const contentKey = `${msg.role}-${msg.content}-${Math.floor(msg.timestamp.getTime() / 1000)}`; // Group by second
          if (!seenContent.has(contentKey)) {
            seenContent.add(contentKey);
            uniqueMessages.push(msg);
          } else {
            console.log('⚠️ Skipping duplicate message:', msg.content.substring(0, 50));
          }
        }
        
        setMessages(uniqueMessages);
        console.log(`✅ Loaded ${uniqueMessages.length} unique messages from Supabase (${loadedMessages.length} total)`);
      }
    } catch (error) {
      console.error('❌ Exception loading messages:', error);
    }
  };

  const showWelcomeMessage = (shouldSave: boolean = false) => {
    // Don't add welcome message if messages already exist
    if (messages.length > 0) {
      console.log('⚠️ Messages already exist, skipping welcome');
      return;
    }
    
    // Customize welcome message based on available files
    let welcomeText = 'Please ask our AI questions about ';
    const capabilities = [];
    
    if (specsFiles && specsFiles.length > 0) {
      capabilities.push(specsFiles.length > 1 ? 'the specs files' : 'the specs');
    }
    capabilities.push('the drawings');
    capabilities.push('the NECA Manual of Labor');
    
    welcomeText += capabilities.join(', ').replace(/, ([^,]*)$/, ', or $1') + '.';
    
    const welcomeMessage: Message = {
      id: generateUUID(),
      role: 'assistant',
      content: welcomeText,
      timestamp: new Date()
    };
    
    console.log('📝 Setting welcome message, shouldSave:', shouldSave);
    setMessages([welcomeMessage]);
    
    // Only save to DB if this is the first time (no existing messages)
    if (shouldSave) {
      console.log('💾 Saving welcome message to Supabase');
      saveMessageToSupabase(welcomeMessage);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleCopyMessage = useCallback(async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setCopiedMessageId(current => (current === messageId ? null : current));
      }, 2000);
    } catch (error) {
      console.error('❌ Failed to copy assistant response', error);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, liveThinkingSteps]);

  // Handle sending a message
  const handleSend = useCallback(async () => {
    if (!input.trim()) return;
    
    // Reset cancelled flag for new request
    cancelledRef.current = false;

    // Add user message
    const userMessage: Message = {
      id: generateUUID(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    saveMessageToSupabase(userMessage); // Save user message to DB
    
    const userQuery = input;
    setInput('');
    setIsProcessing(true);
    setLiveThinkingSteps([]);

    try {
      // Call the main orchestrator agent with function calling
      const { queryMainAgent } = await import('../services/mainAgent');
      
      console.log('🎨 Calling queryMainAgent with drawingPdfUrl:', drawingPdfUrl || '(none)');
      
      const response = await queryMainAgent(
        userQuery,
        specsFiles || [],  // Pass array of all specs files
        (step) => {
          // Don't update thinking steps if cancelled
          if (cancelledRef.current) return;
          
          console.log('💭 Live thought:', step.step);
          setLiveThinkingSteps(prev => [...prev, step]);
        },
        messages.map(m => ({ role: m.role, content: m.content })), // Pass conversation history
        estimation?.id, // estimationId for RFI management
        estimation?.name, // projectName for RFI headers
        estimation?.contract_number, // contractNumber for RFI headers
        drawingPdfUrl // URL to the drawing PDF for text extraction
      );

      // Don't process response if cancelled
      if (cancelledRef.current) {
        console.log('🛑 Request was cancelled, ignoring response');
        return;
      }

      console.log('🔍 Main agent response:', {
        hasAnswer: !!response.answer,
        answerLength: response.answer?.length,
        hasCitations: !!response.citations,
        citationCount: response.citations?.length,
        answerPreview: response.answer?.substring(0, 100)
      });

      // Add assistant response with citations (if tool was called)
      const assistantMessage: Message = {
        id: generateUUID(),
        role: 'assistant',
        content: response.answer || 'No response generated',
        timestamp: new Date(),
        citations: response.citations,
        thinkingSteps: response.thinkingSteps?.map(s => ({ 
          step: s.step, 
          status: 'completed' as const,
          pagesSearched: s.pagesSearched,
          result: s.result
        })),
        confidence: response.confidence as 'high' | 'medium' | 'low' | undefined,
        // Tool responses for display
        drawingResponse: response.drawingResponse,
        specsResponse: response.specsResponse,
        laborEstimate: response.laborEstimate,
        shorthandEstimate: response.shorthandEstimate
      };

      setMessages(prev => [...prev, assistantMessage]);
      saveMessageToSupabase(assistantMessage);
      setLiveThinkingSteps([]);
      if (!isOpenRef.current) {
        onUnreadChangeRef.current?.(true);
      }

      // Auto-redirect to estimate table after labor calculation or shorthand estimate
      if (response.laborEstimate || response.shorthandEstimate) {
        console.log('🔄 Redirecting to estimate table...');
        setTimeout(() => {
          onNavigateToEstimate?.();
        }, 500); // Small delay to ensure message is rendered first
      }

    } catch (error: any) {
      // Don't show error if cancelled
      if (cancelledRef.current) {
        console.log('🛑 Request was cancelled, ignoring error');
        return;
      }
      
      console.error('Error querying specs:', error);
      const errorMessage: Message = {
        id: generateUUID(),
        role: 'assistant',
        content: 'I encountered an error searching the specifications. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      saveMessageToSupabase(errorMessage); // Save error message to DB
    } finally {
      setIsProcessing(false);
      setLiveThinkingSteps([]);
    }
  }, [input, specsFiles, saveMessageToSupabase, messages, estimation, onNavigateToEstimate]);

  // Stop handler - sets flag to ignore response
  const handleStop = useCallback(() => {
    cancelledRef.current = true;
    setIsProcessing(false);
    setLiveThinkingSteps([]);
    console.log('🛑 User cancelled request');
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
  };

  const toggleCitation = (messageId: string, citationIndex: number) => {
    const key = `${messageId}-${citationIndex}`;
    setExpandedCitations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    sendMessage: async (message: string) => {
      setInput(message);
      await new Promise(resolve => setTimeout(resolve, 0));
      await handleSend();
    },
    addMessage: (message: Message | { role?: 'user' | 'assistant'; content: string; citations?: Citation[]; laborEstimate?: any; thinkingSteps?: any[] }) => {
      const newMessage: Message = 'id' in message ? message as Message : {
        id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        role: (message as any).role || 'assistant',
        content: message.content,
        timestamp: new Date(),
        citations: message.citations || [],
        laborEstimate: message.laborEstimate,
        thinkingSteps: message.thinkingSteps
      };
      setMessages(prev => [...prev, newMessage]);
      saveMessageToSupabase(newMessage);
      setTimeout(() => scrollToBottom(), 100);
    },
    updateThinkingSteps: (steps: any[]) => {
      setLiveThinkingSteps(steps);
      setTimeout(() => scrollToBottom(), 50);
    },
    startProcessing: () => {
      setIsProcessing(true);
      setLiveThinkingSteps([]);
    },
    stopProcessing: () => {
      setIsProcessing(false);
      setLiveThinkingSteps([]);
    },
    switchToAssistant: () => {
      setTimeout(() => scrollToBottom(), 50);
    },
  }), [handleSend]);

  // Reset textarea height when input cleared
  useEffect(() => {
    if (!input && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`agent-chat-modal-overlay ${isOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className={`agent-chat-modal ${isOpen ? 'open' : ''}`}>
      <div className="agent-chat-header-new">
        <span style={{ 
          fontSize: '16px', 
          fontWeight: '700', 
          color: 'var(--text-primary)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          {estimation?.name ? estimation.name.split(' ').map((word: string) => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ') : 'AI Assistant'}
        </span>
        <button className="agent-chat-close-btn" onClick={onClose} title="Close">
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div className="chat-messages-modern">
        <>
          {messages.map((message) => {
            const showProofSections = Boolean((message.thinkingSteps?.length ?? 0) || (message.citations?.length ?? 0));
            return (
              <div key={message.id} className={`message-block ${message.role}`}>
                <div className="message-wrapper">
                  {/* User messages - simple display */}
                  {message.role === 'user' && (
                    <>
                      <div style={{
                        background: 'var(--bg-tertiary)',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '15px',
                        color: 'var(--text-primary)',
                        lineHeight: '1.5',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08)'
                      }}>
                        {message.content}
                      </div>
                      
                      {/* Show real-time thinking under last user message */}
                      {isProcessing && messages.indexOf(message) === messages.length - 1 && liveThinkingSteps.length > 0 && (
                        <div style={{
                          fontSize: '13px',
                          color: 'var(--text-placeholder)',
                          fontStyle: 'italic',
                          marginTop: '8px',
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'inline-block'
                        }}>
                          <span style={{ position: 'relative', zIndex: 1 }}>
                            {liveThinkingSteps[liveThinkingSteps.length - 1]?.step}
                          </span>
                          {/* Continuous looping shine effect */}
                          <div style={{
                            position: 'absolute',
                            top: '-2px',
                            left: '-100%',
                            width: '200%',
                            height: 'calc(100% + 4px)',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)',
                            animation: 'thinking-shine 1.5s linear infinite',
                            pointerEvents: 'none',
                            zIndex: 2
                          }} />
                        </div>
                      )}
                    </>
                  )}

                  {/* Assistant messages - show thinking steps as tool calls FIRST (proof of work) */}
                  {message.role === 'assistant' && message.thinkingSteps && message.thinkingSteps.length > 0 && (
                    <div className="tool-call-history">
                      {message.thinkingSteps.map((step, idx) => (
                        <div key={idx} className="tool-call-history-item">
                          <div className="tool-call-header">
                            <span className={`tool-call-status-dot ${step.status === 'completed' ? 'success' : step.status === 'error' ? 'error' : 'pending'}`} />
                            <span className="tool-call-step">{step.step}</span>
                          </div>
                          {step.result && (
                            <div className="tool-call-reasoning-message" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                              {step.result}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show citations as clickable cards SECOND (assistant only) */}
                  {message.role === 'assistant' && message.citations && message.citations.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {message.citations.map((citation, idx) => {
                        return (
                          <div key={idx}>
                            {/* Citation card - always expanded */}
                            <div
                              className="citation-card"
                              style={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                padding: '12px',
                                backgroundColor: 'var(--bg-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08)'
                              }}
                              onClick={(e) => {
                                // Navigate based on citation source
                                if (citation.source === 'drawings') {
                                  if (onNavigateToDrawingPage) {
                                    onNavigateToDrawingPage(citation.pageNumber);
                                  }
                                } else if (citation.source === 'neca_manual') {
                                  if (onNavigateToNecaPage) {
                                    onNavigateToNecaPage(citation.pageNumber);
                                  }
                                } else {
                                  if (onNavigateToSpecsPage) {
                                    onNavigateToSpecsPage(citation);
                                  }
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.position = 'relative';
                                e.currentTarget.style.overflow = 'hidden';
                                
                                // Create shine element
                                const shine = document.createElement('div');
                                shine.className = 'citation-shine';
                                shine.style.cssText = `
                                  position: absolute;
                                  top: 0;
                                  left: -100%;
                                  width: 100%;
                                  height: 100%;
                                  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%);
                                  animation: shine 0.6s ease-in-out;
                                  pointer-events: none;
                                `;
                                e.currentTarget.appendChild(shine);
                                setTimeout(() => shine.remove(), 600);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              {/* Citation Header - Stacked vertically */}
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                marginBottom: '12px'
                              }}>
                                {/* Source badge - Drawing, NECA, or Specs */}
                                {citation.source === 'drawings' ? (
                                  <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: 'var(--green-text)',
                                    backgroundColor: 'var(--green-tint-strong)',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: 'fit-content'
                                  }}>
                                    Drawing{citation.sheetName ? ` - ${citation.sheetName}` : ''}
                                  </div>
                                ) : citation.source === 'neca_manual' ? (
                                  <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: 'var(--purple-text)',
                                    backgroundColor: 'var(--purple-tint-medium)',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: 'fit-content'
                                  }}>
                                    <BookOpen size={13} />
                                    NECA Manual
                                  </div>
                                ) : citation.fileName && (
                                  <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#3b82f6',
                                    backgroundColor: 'var(--blue-tint-light)',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: 'fit-content'
                                  }}>
                                    <FileText size={13} />
                                    {citation.fileName}
                                  </div>
                                )}
                                
                                {/* Page number and section on same line */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: 'var(--text-muted)',
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: '3px 8px',
                                    borderRadius: '4px'
                                  }}>
                                    Page {citation.pageNumber}
                                  </span>
                                  {citation.section && (
                                    <span style={{
                                      fontSize: '12px',
                                      color: 'var(--text-secondary)',
                                      fontWeight: '600',
                                      letterSpacing: '0.01em'
                                    }}>
                                      {citation.section}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Citation quote - always visible */}
                              <div 
                                style={{
                                  fontSize: '13px',
                                  color: 'var(--text-secondary)',
                                  lineHeight: '1.6',
                                  paddingTop: '12px',
                                  borderTop: '1px solid var(--border-color)',
                                  maxHeight: '100px',
                                  overflow: 'hidden',
                                  position: 'relative'
                                }}
                              >
                                <div style={{
                                  fontSize: '13px',
                                  color: 'var(--text-primary)',
                                  lineHeight: '1.6',
                                  marginBottom: '8px',
                                  fontFamily: '"Times New Roman", Times, serif',
                                  backgroundColor: 'var(--bg-primary)',
                                  padding: '12px',
                                  borderRadius: '6px'
                                }}>
                                  {citation.quote}
                                </div>
                                {/* Fade effect at bottom */}
                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: '30px',
                                  background: 'linear-gradient(to bottom, transparent, var(--bg-secondary))',
                                  pointerEvents: 'none'
                                }} />
                              </div>
                            </div>
                            
                            {/* Analysis as message text below citation */}
                            {citation.analysis && (
                              <div className="message-text-modern" style={{ marginTop: '8px' }}>
                                <ReactMarkdown>{citation.analysis}</ReactMarkdown>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Tool responses (drawings/specs) are gathered internally but not displayed separately */}
                  {/* Citations are still collected and available in the response */}

                  {/* Show labor breakdown (assistant only, after processing) */}
                  {message.role === 'assistant' && message.laborEstimate && (
                    <div style={{ marginTop: '12px' }}>
                      <LaborBreakdown
                        estimate={message.laborEstimate}
                        onStepExpand={(pageNumber) => {
                          if (onNavigateToNecaPage) {
                            onNavigateToNecaPage(pageNumber);
                          }
                        }}
                        laborRate={laborRate}
                        materialCosts={new Map()}
                        defaultOverhead={overhead}
                        defaultProfit={profit}
                      />
                    </div>
                  )}

                  {/* Show shorthand breakdown (assistant only, after processing) */}
                  {message.role === 'assistant' && message.shorthandEstimate && (
                    <div style={{ marginTop: '12px' }}>
                      <ShorthandBreakdown
                        materialCost={message.shorthandEstimate.materialCost}
                        laborHours={message.shorthandEstimate.laborHours}
                        pmLaborRate={message.shorthandEstimate.pmLaborRate}
                        pmHours={message.shorthandEstimate.pmHours}
                        laborRate={laborRate}
                        overhead={overhead}
                        profit={profit}
                        materialTaxRate={materialTaxRate}
                        shorthandLaborRate={message.shorthandEstimate.laborRate}
                        shorthandOverhead={message.shorthandEstimate.overhead}
                        shorthandProfit={message.shorthandEstimate.profit}
                        shorthandMaterialTaxRate={message.shorthandEstimate.materialTaxRate}
                      />
                    </div>
                  )}

                  {/* Final answer (orchestrator's summary) - hide for labor estimates since table shows all info */}
                  {message.role === 'assistant' && message.content && !message.laborEstimate && (
                    <div
                      className={`message-text-modern assistant-final-response ${(showProofSections || message.laborEstimate || message.shorthandEstimate) ? 'assistant-final-response--with-proof' : ''}`}
                      style={{
                        marginTop: (showProofSections || message.laborEstimate || message.shorthandEstimate) ? '16px' : '0',
                        paddingTop: (message.laborEstimate || message.shorthandEstimate) ? '12px' : '0',
                        borderTop: (showProofSections || message.laborEstimate || message.shorthandEstimate) ? '1px solid var(--border-color)' : 'none'
                      }}
                    >
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                      <div className="assistant-final-response-actions">
                        <button
                          type="button"
                          className={`message-copy-button ${copiedMessageId === message.id ? 'copied' : ''}`}
                          onClick={() => handleCopyMessage(message.id, message.content)}
                          aria-label={copiedMessageId === message.id ? 'Response copied' : 'Copy response'}
                        >
                          {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                          <span>{copiedMessageId === message.id ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            );
          })}
        </>

        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      <div className="agent-chat-input-container">
        <div className="agent-chat-input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Ask Bidflow questions about your takeoff"
            className="agent-chat-input"
            rows={1}
            disabled={isProcessing}
            style={{
              resize: 'none',
              overflow: 'hidden'
            }}
          />
          {isProcessing ? (
            <button
              onClick={handleStop}
              className="agent-chat-send-btn"
              title="Stop generation"
            >
              <Square size={14} fill="currentColor" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="agent-chat-send-btn"
              style={{
                opacity: !input.trim() ? 0.5 : 1,
                cursor: !input.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              <ArrowUp size={18} />
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
});

NewAgentChat.displayName = 'NewAgentChat';

export default NewAgentChat;

